/**
 * RecentOpponents Component - Displays recent players you've played against
 */

import { useState, useEffect } from 'react';
import { subscribeToRecentOpponents, sendFriendRequest, areFriends } from '../services/friendService';
import { safeString } from '../utils/safeString';
import '../styles/RecentOpponents.css';

const RecentOpponents = ({ userId, userName, userPhoto }) => {
  const [opponents, setOpponents] = useState([]);
  const [sendingRequest, setSendingRequest] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [friendStatus, setFriendStatus] = useState({});

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToRecentOpponents(userId, (updatedOpponents) => {
      setOpponents(updatedOpponents);

      // Check friend status for each opponent
      updatedOpponents.forEach(async (opponent) => {
        const isFriend = await areFriends(userId, opponent.userId);
        setFriendStatus(prev => ({ ...prev, [opponent.userId]: isFriend }));
      });
    });

    return () => unsubscribe();
  }, [userId]);

  const handleAddFriend = async (opponent) => {
    if (sendingRequest) return;

    setOpenMenu(null);
    setSendingRequest(opponent.id);

    try {
      await sendFriendRequest(
        userId,
        userName,
        userPhoto,
        opponent.userId,
        opponent.displayName
      );

      alert(`Friend request sent to ${safeString(opponent.displayName, 'opponent')}!`);

      // Update friend status
      setFriendStatus(prev => ({ ...prev, [opponent.userId]: 'pending' }));
    } catch (error) {
      console.error('Error sending friend request:', error);
      alert(error.message || 'Failed to send friend request');
    } finally {
      setSendingRequest(null);
    }
  };

  const toggleMenu = (opponentId) => {
    setOpenMenu(openMenu === opponentId ? null : opponentId);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';

    const now = Date.now();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) {
      return 'Less than an hour ago';
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else if (days < 7) {
      return `${days}d ago`;
    } else {
      return new Date(timestamp).toLocaleDateString();
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'won':
        return '🏆';
      case 'lost':
        return '😞';
      case 'draw':
        return '🤝';
      default:
        return '🎮';
    }
  };

  const getResultText = (result) => {
    switch (result) {
      case 'won':
        return 'You won';
      case 'lost':
        return 'You lost';
      case 'draw':
        return 'Draw';
      default:
        return 'Played';
    }
  };

  return (
    <div className="recent-opponents">
      <div className="recent-opponents-header">
        <h3>RECENT OPPONENTS</h3>
      </div>

      <div className="recent-opponents-content">
        {opponents.length === 0 ? (
          <div className="recent-opponents-empty">
            <p>No recent opponents</p>
            <small>Play some games to see your opponents here!</small>
          </div>
        ) : (
          opponents.map((opponent) => {
            const isFriend = friendStatus[opponent.userId];
            const menuOpen = openMenu === opponent.id;

            return (
              <div key={opponent.id} className="opponent-item">
                <div className="opponent-item-user">
                  {opponent.photoURL && (
                    <img
                      src={safeString(opponent.photoURL, '')}
                      alt={safeString(opponent.displayName, 'Opponent')}
                      className="opponent-avatar"
                    />
                  )}
                  <div className="opponent-info">
                    <h4 className="opponent-name">{safeString(opponent.displayName, 'Unknown Player')}</h4>
                    <p className="opponent-stats">
                      <span className="opponent-result">
                        {getResultIcon(opponent.lastResult)} {getResultText(opponent.lastResult)}
                      </span>
                      {' • '}
                      <span className="opponent-game">{opponent.lastGame || 'Unknown'}</span>
                    </p>
                    <p className="opponent-time">{formatTime(opponent.lastPlayed)}</p>
                  </div>
                </div>

                <div className="opponent-menu-wrapper">
                  <button
                    className="opponent-menu-btn"
                    onClick={() => toggleMenu(opponent.id)}
                    title="More options"
                  >
                    ⋮
                  </button>

                  {menuOpen && (
                    <div className="opponent-menu-dropdown">
                      {!isFriend && (
                        <button
                          className="opponent-menu-item"
                          onClick={() => handleAddFriend(opponent)}
                          disabled={sendingRequest === opponent.id}
                        >
                          {sendingRequest === opponent.id ? '⏳ Sending...' : '➕ Send Friend Request'}
                        </button>
                      )}
                      {isFriend && (
                        <div className="opponent-menu-item disabled">
                          ✓ Already Friends
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RecentOpponents;
